import { useEffect, useState } from 'react';
import { ordersService } from '../services/ordersService';
import { fingerprintQuoteRequest } from '../utils/storefrontOrder/payload';

const PREVIEW_DEBOUNCE_MS = 400;

// Dry-runs a staged order edit with debouncing. Results are keyed by the
// fingerprint of the body they answer, so a changed body reads as loading
// immediately and an older, slower response can never be shown as current.
export function useOrderEditPreview(orderId, body) {
  const [result, setResult] = useState({ key: null, preview: null, error: null });
  const fingerprint = body ? fingerprintQuoteRequest(body) : null;
  const key = fingerprint ? `${orderId}:${fingerprint}` : null;

  useEffect(() => {
    if (!fingerprint) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const preview = await ordersService.previewOrderEdit(orderId, JSON.parse(fingerprint));
        if (!cancelled) setResult({ key, preview, error: null });
      } catch (err) {
        if (!cancelled) {
          setResult({
            key,
            preview: null,
            error: err.message || 'Unable to preview order changes'
          });
        }
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderId, fingerprint, key]);

  if (!fingerprint) return { preview: null, status: 'idle', error: null };
  if (result.key !== key) return { preview: null, status: 'loading', error: null };
  if (result.error) return { preview: null, status: 'error', error: result.error };
  return { preview: result.preview, status: 'ready', error: null };
}
